import * as Redux from 'redux';

import { addTime } from '../../../utils/dateTimeUtils';
import {
  CollectionRestaurantDetail,
  CollectionRestaurantSummary,
  DeliveryRestaurantDetail,
  DeliveryRestaurantSummary,
} from '../../../weborder/Api';
import * as Store from '../../../weborder/Store';
import { fdApi } from '../../api';
import { Async, AxiosError } from '../../types/data.types';
import { OrderType, Status } from '../../types/enum.types';
import { store } from '.';
import { Campaign } from './store.types';
import {
  setCampaignProgress,
  setRestaurantCollection,
  setRestaurantDelivery,
} from './storeActions';

type QueryParams = {
  count: number;
  includePhysicalRestaurantIds?: string;
  Latitude: number;
  Longitude: number;
  skip: number;
};

export const getCollectionSummaries =
  () =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ): Promise<void> => {
    const {
      Config,
      Location: { geo },
    } = getState().flipdish;

    const collectionParams: QueryParams = {
      count: 1000,
      Latitude: geo.Latitude,
      Longitude: geo.Longitude,
      skip: 0,
    };

    if (Config.IncludePhysicalRestaurantIds) {
      collectionParams.includePhysicalRestaurantIds =
        Config.IncludePhysicalRestaurantIds;
    }

    await fdApi
      .use(
        fdApi.store.getCollectionSummaries({
          params: collectionParams,
        }),
      )
      .then((data: CollectionRestaurantSummary[]) => {
        dispatch(
          store.actions.setCollectionSummaries({
            data,
            status: 'success',
          }),
        );
      })
      .catch((_error: AxiosError) => {
        dispatch(
          store.actions.setCollectionSummaries({
            status: 'none',
          }),
        );
      });
  };

export const getDeliverySummaries =
  () =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ): Promise<void> => {
    const {
      Config,
      Location: { geo },
    } = getState().flipdish;

    const deliveryParams = {
      count: 1000,
      deviceId: Config.DeviceId,
      Latitude: geo.Latitude,
      Longitude: geo.Longitude,
      skip: 0,
    };

    await fdApi
      .use(fdApi.store.getDeliverySummaries({ params: deliveryParams }))
      .then((data: DeliveryRestaurantSummary[]) => {
        dispatch(
          store.actions.setDeliverySummaries({
            data,
            status: 'success',
          }),
        );
      })
      .catch((_error: AxiosError) => {
        store.actions.setDeliverySummaries({
          status: 'none',
        });
      });
  };

export const getRestaurantDetail =
  (
    vrId: number,
    orderType: OrderType.COLLECTION | OrderType.DELIVERY,
    id: number,
  ) =>
  (
    dispatch: Redux.Dispatch<Redux.Action>,
    _getState: () => Store.State,
  ): void => {
    const dispatchThunk = dispatch as any;

    if (orderType === OrderType.COLLECTION) {
      dispatchThunk(getCollectionRestaurantDetail(id));
    } else {
      dispatchThunk(getDeliveryRestaurantDetail(vrId, id));
    }
  };

export const getCollectionRestaurantDetail =
  (prId: number) =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    _getState: () => Store.State,
  ): Promise<void> => {
    const dispatchSetRestaurantCollection = (
      collectionRestaurant: Async<CollectionRestaurantDetail>,
    ): Store.SetRestaurantCollection =>
      dispatch(setRestaurantCollection(prId, collectionRestaurant));

    dispatchSetRestaurantCollection({ status: Status.LOADING });

    await fdApi
      .use(fdApi.store.getCollectionDetail(`${prId}`))
      .then((data: CollectionRestaurantDetail) => {
        const closingTimeStamp = data?.SecondsUntilRestaurantCloses
          ? addTime(new Date(), 'seconds', data.SecondsUntilRestaurantCloses)
          : undefined;

        const customData = {
          ...data,
          ClosingTimeStamp: closingTimeStamp,
          // TODO - CXFE-1857 remove FromAPI from code, it should always have the latest from BE
          FromAPI: true,
        };

        dispatchSetRestaurantCollection({
          status: Status.SUCCESS,
          data: customData,
        });
      })
      .catch((error: AxiosError) => {
        dispatchSetRestaurantCollection({
          status: Status.ERROR,
          error,
        });
      });
  };

export const getDeliveryRestaurantDetail =
  (vrId: number, deliveryLocationId: number) =>
  async (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ): Promise<void> => {
    const dispatchSetRestaurantDelivery = (
      deliveryRestaurant: Async<DeliveryRestaurantDetail>,
    ): Store.SetRestaurantDelivery =>
      dispatch(
        setRestaurantDelivery(deliveryLocationId, deliveryRestaurant, vrId),
      );

    dispatchSetRestaurantDelivery({ status: Status.LOADING });

    const query = {
      deviceId: getState().flipdish.Config.DeviceId,
      deliveryLocationId,
    };
    const options = {
      params: query,
      retry: 3,
      retryDelay: 1000,
    };

    await fdApi
      .use(fdApi.store.getDeliveryDetail(vrId, options))
      .then((data: DeliveryRestaurantDetail) => {
        const closingTimeStamp = data?.SecondsUntilRestaurantCloses
          ? addTime(new Date(), 'seconds', data.SecondsUntilRestaurantCloses)
          : undefined;

        const customData = {
          ...data,
          ClosingTimeStamp: closingTimeStamp,
          // TODO - CXFE-1857 remove FromAPI from code, it should always have the latest from BE
          FromAPI: true,
        };

        dispatchSetRestaurantDelivery({
          status: Status.SUCCESS,
          data: customData,
        });
      })
      .catch((error: AxiosError) => {
        dispatchSetRestaurantDelivery({
          status: Status.ERROR,
          error,
        });
      });
  };

export const getCampaignProgress =
  (appId: string, storeId: number) =>
  async (dispatch: Redux.Dispatch<Redux.Action>): Promise<void> =>
    fdApi
      .use(fdApi.store.getCampaignProgress(appId, storeId))
      .then((data: Campaign[]) => {
        dispatch(
          setCampaignProgress({
            data,
            status: 'success',
            storeId,
          }),
        );
      })
      .catch((_error: AxiosError) => {
        setCampaignProgress({
          status: 'none',
          storeId,
        });
      });
